import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, ImageOff } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { API_BASE_URL } from '../../config';
import { apiClient } from '../../services/api';
import { adminStoresAPI } from '../../utils/api';

const RUTA = `${API_BASE_URL}/api/admin/price-showcase`;
const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

/**
 * Muestrario de precios reales — el editor de Iván (decisión 39).
 *
 * Cada renglón es una prenda con su foto, lo que cuesta en tienda y lo que
 * costaría con vondi. La app enseña esas tres cosas en la home, y el ahorro lo
 * calcula sola: **no se captura**, para que no pueda contradecir a los precios.
 *
 * La foto se sube como archivo (PNG recortado, sin fondo, como en el mockup).
 * Va por `FormData` y por eso la actualización usa POST: un PUT con archivo no
 * llega completo, que es el mismo motivo por el que el editor de tiendas hace
 * method spoofing.
 */
export default function PriceShowcaseManagement() {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [nueva, setNueva] = useState({ store_id: '', store_name: '', product_name: '', price_regular: '', price_vondi: '' });
  const [archivo, setArchivo] = useState(null);
  /* Catálogo de tiendas: enlazar la prenda a una hace que la tarjeta herede su
     logo, el mismo que las flip cards ponen en la zona C. */
  const [tiendas, setTiendas] = useState([]);

  const cargar = async () => {
    try {
      const r = await apiClient.get(RUTA);
      setLista(r?.data ?? []);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar el muestrario', variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    adminStoresAPI.getAll()
      .then((r) => setTiendas(r?.data ?? []))
      .catch(() => setTiendas([]));
  }, []);

  /* Al elegir tienda se rellena el nombre, pero se deja editable: el catálogo
     puede decir «Adolfo Domínguez Outlet» y la tarjeta querer decir solo
     «ADOLFO DOMÍNGUEZ». */
  const elegirTienda = (id) => {
    const t = tiendas.find((x) => String(x.id) === String(id));
    setNueva((n) => ({
      ...n,
      store_id: id,
      store_name: t && !n.store_name.trim() ? t.name : n.store_name,
    }));
  };

  const regular = Number(nueva.price_regular);
  const conVondi = Number(nueva.price_vondi);
  const ahorro = regular > 0 && conVondi >= 0 && conVondi <= regular ? regular - conVondi : null;

  const agregar = async () => {
    if (!nueva.store_name.trim()) {
      toast({ title: 'Falta la marca', description: 'Escribe de qué tienda es la prenda.', variant: 'destructive' });
      return;
    }
    if (!(regular > 0) || !(conVondi >= 0)) {
      toast({ title: 'Faltan los precios', description: 'Los dos precios son obligatorios.', variant: 'destructive' });
      return;
    }
    if (conVondi > regular) {
      toast({ title: 'Revisa los precios', description: 'El precio con vondi tiene que ser menor que el de tienda.', variant: 'destructive' });
      return;
    }

    setGuardando(true);
    try {
      const datos = new FormData();
      if (nueva.store_id) datos.append('store_id', nueva.store_id);
      datos.append('store_name', nueva.store_name.trim());
      if (nueva.product_name.trim()) datos.append('product_name', nueva.product_name.trim());
      datos.append('price_regular', String(regular));
      datos.append('price_vondi', String(conVondi));
      if (archivo) datos.append('image', archivo);

      await apiClient.request(RUTA, { method: 'POST', body: datos });

      setNueva({ store_id: '', store_name: '', product_name: '', price_regular: '', price_vondi: '' });
      setArchivo(null);
      await cargar();
      toast({ title: 'Prenda agregada' });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const alternarVisible = async (item) => {
    const datos = new FormData();
    datos.append('is_active', item.is_active ? '0' : '1');
    try {
      await apiClient.request(`${RUTA}/${item.id}`, { method: 'POST', body: datos });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo actualizar', variant: 'destructive' });
    }
  };

  const eliminar = async (item) => {
    if (!window.confirm(`¿Quitar la prenda de ${item.store_name} del muestrario?`)) return;
    try {
      await apiClient.delete(`${RUTA}/${item.id}`);
      await cargar();
      toast({ title: 'Prenda eliminada' });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  /* Mover una prenda arriba o abajo. El orden importa: es el orden en que el
     visitante las desliza, y la primera es la única que ve seguro. */
  const mover = async (indice, direccion) => {
    const destino = indice + direccion;
    if (destino < 0 || destino >= lista.length) return;

    const nuevoOrden = [...lista];
    [nuevoOrden[indice], nuevoOrden[destino]] = [nuevoOrden[destino], nuevoOrden[indice]];
    setLista(nuevoOrden);

    try {
      await apiClient.post(`${RUTA}/reorder`, { ids: nuevoOrden.map((i) => i.id) });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar el orden', variant: 'destructive' });
      cargar();
    }
  };

  const imagenDe = (item) =>
    item.image ? (item.image.startsWith('http') ? item.image : `${API_BASE_URL}${item.image}`) : null;

  if (cargando) {
    return <div className="p-8 text-gray-500">Cargando muestrario...</div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Muestrario de precios reales</h2>
          <p className="text-gray-500">
            «Lo que cuesta sin vondi y con vondi.» Es una <strong>pestaña propia</strong> dentro de la app del
            usuario. Sube el PNG de la prenda recortado, sin fondo: la tarjeta ya trae el color.
          </p>
        </div>

        {/* Alta */}
        <div className="bg-white border border-gray-200 rounded-3xl p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-900">Tienda del catálogo</Label>
              <select
                value={nueva.store_id}
                onChange={(e) => elegirTienda(e.target.value)}
                className="w-full bg-gray-50 border border-gray-300 rounded-2xl h-12 px-4 text-gray-900"
              >
                <option value="">Sin enlazar (solo el nombre)</option>
                {tiendas.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                Enlazándola, la tarjeta usa el logo de esa tienda en vez del nombre escrito.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-900">Marca *</Label>
              <Input
                value={nueva.store_name}
                onChange={(e) => setNueva({ ...nueva, store_name: e.target.value })}
                placeholder="Ej: Adolfo Domínguez"
                className="bg-gray-50 border-gray-300 rounded-2xl h-12"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-900">Prenda (opcional)</Label>
              <Input
                value={nueva.product_name}
                onChange={(e) => setNueva({ ...nueva, product_name: e.target.value })}
                placeholder="Ej: Playera negra"
                className="bg-gray-50 border-gray-300 rounded-2xl h-12"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-900">Precio en tienda *</Label>
              <Input
                type="number" min="0" step="0.01"
                value={nueva.price_regular}
                onChange={(e) => setNueva({ ...nueva, price_regular: e.target.value })}
                placeholder="2400"
                className="bg-gray-50 border-gray-300 rounded-2xl h-12"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-900">Precio con vondi *</Label>
              <Input
                type="number" min="0" step="0.01"
                value={nueva.price_vondi}
                onChange={(e) => setNueva({ ...nueva, price_vondi: e.target.value })}
                placeholder="1920"
                className="bg-gray-50 border-gray-300 rounded-2xl h-12"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-1">
              <Label className="text-gray-900">Foto de la prenda</Label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                className="block text-sm text-gray-600"
              />
            </div>

            {/* El ahorro se enseña aquí para que se vea antes de guardar: es el
                número que el cliente va a leer, y un dedazo en los precios se
                cacha mirando esto. */}
            {ahorro !== null && (
              <p className="text-sm text-gray-600">
                El cliente verá <strong className="text-gray-900">Ahorras {pesos(ahorro)}</strong>
              </p>
            )}
          </div>

          <Button onClick={agregar} disabled={guardando} className="bg-black text-white hover:bg-black/90 rounded-full px-6">
            <Plus className="w-4 h-4 mr-2" />
            {guardando ? 'Guardando...' : 'Agregar prenda'}
          </Button>
        </div>

        {/* Lista */}
        {lista.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>El muestrario está vacío</p>
            <p className="text-sm mt-2">Mientras no haya prendas, la sección no se muestra en la app.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {lista.map((item, i) => (
              <div
                key={item.id}
                className={`bg-white border border-gray-200 rounded-3xl p-4 flex items-center gap-4 ${item.is_active ? '' : 'opacity-60'}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                  {imagenDe(item) ? (
                    <img src={imagenDe(item)} alt={item.store_name} className="w-full h-full object-contain" />
                  ) : (
                    <ImageOff className="w-5 h-5 text-gray-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {item.store_name}
                    {item.product_name && <span className="text-gray-500 font-normal"> · {item.product_name}</span>}
                  </p>
                  <p className="text-sm text-gray-500">
                    {item.store_logo && <span className="text-gray-700">con logo · </span>}
                    <span className="line-through">{pesos(item.price_regular)}</span>
                    {' → '}
                    <strong className="text-gray-900">{pesos(item.price_vondi)}</strong>
                    {' · '}
                    Ahorras {pesos(item.savings ?? item.price_regular - item.price_vondi)}
                  </p>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => mover(i, -1)} disabled={i === 0} title="Subir"
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30">
                    <ArrowUp className="w-4 h-4 text-gray-600" />
                  </button>
                  <button onClick={() => mover(i, 1)} disabled={i === lista.length - 1} title="Bajar"
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30">
                    <ArrowDown className="w-4 h-4 text-gray-600" />
                  </button>
                  <button onClick={() => alternarVisible(item)} title={item.is_active ? 'Ocultar' : 'Mostrar'}
                    className="p-2 rounded-full hover:bg-gray-100">
                    {item.is_active
                      ? <Eye className="w-4 h-4 text-gray-600" />
                      : <EyeOff className="w-4 h-4 text-gray-400" />}
                  </button>
                  <button onClick={() => eliminar(item)} title="Eliminar"
                    className="p-2 rounded-full hover:bg-red-50">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
